// Stammdaten "Transportarten": Auf welchem Weg wird ein Produkt/Code an den
// Empfaenger ausgeliefert (z. B. Physikalisch, WhatsApp, Email, Cloudlink).
// Die hier gepflegten Eintraege stehen im Dienst-Dialog als Mehrfachauswahl
// zur Verfuegung. Aufbau und Bedienung folgen den uebrigen Stammdaten-Boards.

import React, { useState, useEffect, useCallback } from "react";
import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import { Table, Button, Container, Modal, Form, ButtonGroup, Pagination, Spinner, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ErrorModal from './ErrorModal';

const emptyForm = {
  transport_type_name: "",
  transport_code: "",
  is_physical: false,
  is_active: true,
  sort_order: "",
};

// Wandelt einen Datensatz in ein vollstaendig kontrolliertes Formularobjekt um.
const normalize = (row = {}) => ({
  ...emptyForm,
  transport_type_name: row.transport_type_name || "",
  transport_code: row.transport_code || "",
  is_physical: row.is_physical === true || row.is_physical === 1,
  is_active: !(row.is_active === false || row.is_active === 0),
  sort_order: row.sort_order === null || row.sort_order === undefined ? "" : `${row.sort_order}`,
});

const formatDate = (value) => {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
};

const BoardTransportTypes = () => {
  const [transportTypes, setTransportTypes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reloadSignal, setReloadSignal] = useState(false);
  const [errors, setErrors] = useState({});
  const [apierrors, setApiErrors] = useState('');
  const [errorCode, setApiErrorCode] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'sort_order', direction: 'asc' });
  const [filterTerm, setFilterTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(' Lade, bitte warten...');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [formData, setFormData] = useState({ ...emptyForm });

  // Einheitliche, verstaendliche Fehlermeldung aus einer Server-Antwort bauen.
  const showApiError = useCallback((error, fallback) => {
    const serverMessage =
      (error && error.response && error.response.data && error.response.data.message) ||
      (error && error.message) ||
      fallback;
    setApiErrors(serverMessage);
    setApiErrorCode(error && error.response ? `HTTP ${error.response.status}` : '');
    setShowModal(true);
    if (error && error.response && error.response.status === 401) {
      EventBus.dispatch("logout");
    }
  }, []);

  useEffect(() => {
    let active = true;
    UserService.getTransportTypesBoard()
      .then((response) => {
        if (!active) return;
        setTransportTypes(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        if (!active) return;
        setTransportTypes([]);
        showApiError(error, 'Die Transportarten konnten nicht geladen werden.');
      });
    return () => { active = false; };
  }, [reloadSignal, showApiError]);

  const validateForm = (data) => {
    const newErrors = {};
    if (!data.transport_type_name || !data.transport_type_name.trim()) {
      newErrors.transport_type_name = 'Bitte einen Namen für die Transportart angeben.';
    } else if (data.transport_type_name.trim().length > 100) {
      newErrors.transport_type_name = 'Der Name darf höchstens 100 Zeichen lang sein.';
    }
    const code = (data.transport_code || '').trim();
    if (code && !/^[A-Za-z0-9_]{1,32}$/.test(code)) {
      newErrors.transport_code = 'Nur Buchstaben, Ziffern und Unterstriche, höchstens 32 Zeichen.';
    }
    const order = `${data.sort_order ?? ''}`.trim();
    if (order && !/^\d{1,6}$/.test(order)) {
      newErrors.sort_order = 'Bitte eine ganze Zahl angeben.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const filtered = Array.isArray(transportTypes)
    ? transportTypes.filter((row) => {
        const term = filterTerm.trim().toLowerCase();
        if (!term) return true;
        return (
          (row.transport_type_name || '').toLowerCase().includes(term) ||
          (row.transport_code || '').toLowerCase().includes(term) ||
          `${row.transport_type_id}` === term
        );
      })
    : [];

  const sorted = [...filtered].sort((a, b) => {
    const key = sortConfig.key;
    const av = a[key] === null || a[key] === undefined ? '' : a[key];
    const bv = b[key] === null || b[key] === undefined ? '' : b[key];
    if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
    if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const currentRows = sorted.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleCreateClick = () => {
    setApiErrors('');
    setApiErrorCode('');
    setErrors({});
    setFormData({ ...emptyForm });
    setShowCreateModal(true);
  };

  const handleEditClick = (row) => {
    setApiErrors('');
    setApiErrorCode('');
    setErrors({});
    setSelected(row);
    setFormData(normalize(row));
    setShowEditModal(true);
  };

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Baut den Datensatz fuer die API: leerer Schluessel/Reihenfolge wird zu null.
  const toPayload = (data) => ({
    transport_type_name: (data.transport_type_name || '').trim(),
    transport_code: (data.transport_code || '').trim() || null,
    is_physical: !!data.is_physical,
    is_active: !!data.is_active,
    sort_order: `${data.sort_order ?? ''}`.trim() === '' ? null : Number(data.sort_order),
  });

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm(formData)) return;
    try {
      setLoadingText('Transportart wird gespeichert, bitte warten...');
      setLoading(true);
      await UserService.createTransportTypeBoard(toPayload(formData));
      setShowCreateModal(false);
      setReloadSignal((prev) => !prev);
    } catch (error) {
      showApiError(error, 'Die Transportart konnte nicht angelegt werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm(formData)) return;
    try {
      setLoadingText('Transportart wird aktualisiert, bitte warten...');
      setLoading(true);
      await UserService.updateTransportTypeBoard(selected.transport_type_id, toPayload(formData));
      setShowEditModal(false);
      setReloadSignal((prev) => !prev);
    } catch (error) {
      showApiError(error, 'Die Transportart konnte nicht gespeichert werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (row, e) => {
    e.preventDefault();
    const confirmed = window.confirm(
      `Soll die Transportart „${row.transport_type_name}“ wirklich gelöscht werden?`
    );
    if (!confirmed) return;
    try {
      setLoadingText('Transportart wird gelöscht, bitte warten...');
      setLoading(true);
      await UserService.deleteTransportTypeBoard(row.transport_type_id);
      setReloadSignal((prev) => !prev);
    } catch (error) {
      // Der Server lehnt das Löschen ab, solange die Transportart noch einem
      // Dienst zugeordnet ist, und nennt die Anzahl der betroffenen Dienste.
      showApiError(error, 'Die Transportart konnte nicht gelöscht werden.');
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => (
    <>
      <Form.Group controlId="transportTypeName">
        <Form.Label>Bezeichnung</Form.Label>
        <Form.Control
          type="text"
          placeholder="z. B. WhatsApp"
          name="transport_type_name"
          value={formData.transport_type_name}
          onChange={handleChange}
          isInvalid={!!errors.transport_type_name}
          maxLength={100}
        />
        <Form.Control.Feedback type="invalid">{errors.transport_type_name}</Form.Control.Feedback>
        <Form.Text muted>Dieser Name erscheint im Dienst-Dialog zur Auswahl.</Form.Text>
      </Form.Group>

      <Form.Group controlId="transportTypeCode" className="mt-3">
        <Form.Label>Technischer Schlüssel (optional)</Form.Label>
        <Form.Control
          type="text"
          placeholder="z. B. WHATSAPP"
          name="transport_code"
          value={formData.transport_code}
          onChange={handleChange}
          isInvalid={!!errors.transport_code}
          maxLength={32}
        />
        <Form.Control.Feedback type="invalid">{errors.transport_code}</Form.Control.Feedback>
        <Form.Text muted>
          Bleibt gleich, auch wenn die Bezeichnung später geändert wird. Dient als
          Bezugspunkt für Übersetzungen und Schnittstellen.
        </Form.Text>
      </Form.Group>

      <Form.Group controlId="transportTypeSortOrder" className="mt-3">
        <Form.Label>Reihenfolge (optional)</Form.Label>
        <Form.Control
          type="number"
          min="0"
          step="10"
          placeholder="z. B. 20"
          name="sort_order"
          value={formData.sort_order}
          onChange={handleChange}
          isInvalid={!!errors.sort_order}
        />
        <Form.Control.Feedback type="invalid">{errors.sort_order}</Form.Control.Feedback>
        <Form.Text muted>Kleinere Zahlen erscheinen in Auswahllisten weiter oben.</Form.Text>
      </Form.Group>

      <Form.Group controlId="transportTypeIsPhysical" className="mt-3">
        <Form.Check
          type="checkbox"
          name="is_physical"
          checked={!!formData.is_physical}
          onChange={handleChange}
          label="Physikalische Auslieferung (gedruckter Code / Trägermaterial)"
        />
        <Form.Text muted>
          Dienste, die ausschließlich physikalisch ausgeliefert werden, sind nicht
          digital selbst buchbar. Diese Einstufung wird von der Plattform
          ausgewertet – nicht die Bezeichnung.
        </Form.Text>
      </Form.Group>

      <Form.Group controlId="transportTypeIsActive" className="mt-3">
        <Form.Check
          type="checkbox"
          name="is_active"
          checked={!!formData.is_active}
          onChange={handleChange}
          label="Aktiv (zur Auswahl in Diensten freigegeben)"
        />
        <Form.Text muted>
          Inaktive Transportarten bleiben bei bereits zugeordneten Diensten
          erhalten, können aber nicht neu ausgewählt werden.
        </Form.Text>
      </Form.Group>
    </>
  );

  return (
    <Container>
      <h1 className="mt-5">Transportarten</h1>
      <p className="text-muted">
        Legt fest, auf welchen Wegen ein Produkt beziehungsweise ein Code an den
        Empfänger ausgeliefert werden kann. Die Einträge stehen im Dienst-Dialog
        als Mehrfachauswahl zur Verfügung.
      </p>

      <ErrorModal show={showModal} handleClose={() => setShowModal(false)} errorCode={errorCode} errorMessage={apierrors} />

      <Button variant="info" onClick={handleCreateClick} className="mr-2 mb-2">
        Erstellen
      </Button>

      {loading && (
        <div className="spinner-overlay">
          <div className="spinner-background">
            <Spinner animation="border" role="status" className="spinner-border">
              <span className="sr-only">Lade...</span>
            </Spinner>
            <div className="spinner-text">...{loadingText}</div>
          </div>
        </div>
      )}

      <Form.Group controlId="transportTypeSearch">
        <Form.Control
          type="text"
          placeholder="Suche nach Bezeichnung, Schlüssel oder ID"
          value={filterTerm}
          onChange={(e) => { setFilterTerm(e.target.value); setCurrentPage(1); }}
        />
      </Form.Group>

      <div className="table-responsive">
        <Table striped bordered hover className="mt-4">
          <thead>
            <tr>
              <th onClick={() => handleSort('transport_type_id')} style={{ cursor: 'pointer' }}>
                ID {sortConfig.key === 'transport_type_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('transport_type_name')} style={{ cursor: 'pointer' }}>
                Bezeichnung {sortConfig.key === 'transport_type_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('transport_code')} style={{ cursor: 'pointer' }}>
                Schlüssel {sortConfig.key === 'transport_code' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>Art</th>
              <th>Status</th>
              <th onClick={() => handleSort('sort_order')} style={{ cursor: 'pointer' }}>
                Reihenfolge {sortConfig.key === 'sort_order' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('rec_date')} style={{ cursor: 'pointer' }}>
                Erstelldatum {sortConfig.key === 'rec_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted">
                  Keine Transportarten vorhanden.
                </td>
              </tr>
            )}
            {currentRows.map((row) => (
              <tr key={row.transport_type_id}>
                <td>{row.transport_type_id}</td>
                <td>{row.transport_type_name}</td>
                <td>{row.transport_code || '–'}</td>
                <td>
                  {row.is_physical
                    ? <Badge bg="secondary">Physikalisch</Badge>
                    : <Badge bg="primary">Digital</Badge>}
                </td>
                <td>
                  {row.is_active
                    ? <Badge bg="success">Aktiv</Badge>
                    : <Badge bg="warning" text="dark">Inaktiv</Badge>}
                </td>
                <td>{row.sort_order === null || row.sort_order === undefined ? '–' : row.sort_order}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.rec_date)}</td>
                <td>
                  <ButtonGroup>
                    <Button variant="warning" onClick={() => handleEditClick(row)} className="mr-2" title="Bearbeiten" aria-label="Bearbeiten">
                      <FaEdit />
                    </Button>
                    <Button variant="danger" onClick={(e) => handleDeleteClick(row, e)} title="Löschen" aria-label="Löschen">
                      <FaTrash />
                    </Button>
                  </ButtonGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination>
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
          {[...Array(totalPages)].map((_, index) => (
            <Pagination.Item
              key={index}
              active={index + 1 === currentPage}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      )}

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Transportart erstellen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateSubmit}>
            {renderFormFields()}
            <Button variant="primary" type="submit" className="mt-3" disabled={loading}>
              {loading ? 'Wird gespeichert...' : 'Transportart speichern'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Transportart bearbeiten</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            {renderFormFields()}
            <Button variant="primary" type="submit" className="mt-3" disabled={loading}>
              {loading ? 'Wird aktualisiert...' : 'Transportart aktualisieren'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BoardTransportTypes;
